import { AfterViewChecked, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { BaseComponent } from '~/pages/base.component';
import { ApiService } from '~/services/api.service';
import { GraphService } from '~/core/services/graph.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import {
    DashboardApprovalRow,
    DashboardRequestRow,
} from '~/models/dashboard.model';
import { AudienceSelection } from '~/models/campaign.model';

Chart.register(...registerables);

type SubView = 'requests' | 'approvals';

// Chevron brand palette (mirrors CSS variables in styles.scss).
const CHEVRON = {
    darkBlue: 'rgb(11, 45, 113)',
    mediumBlue: 'rgb(0, 102, 178)',
    lightBlue: 'rgb(0, 157, 217)',
    mediumGreen: 'rgb(118, 146, 49)',
    lightGreen: 'rgb(178, 204, 52)',
    lightRed: 'rgb(228, 24, 54)',
    mediumRed: 'rgb(151, 0, 46)',
    orange: 'rgb(250, 171, 24)',
    gray: 'rgb(140, 143, 147)',
    lightGray: 'rgb(219, 220, 221)',
};

interface RecipientBar {
    name: string;
    count: number;
}

interface ApproverCampaign {
    campaignId: string;
    name: string;
    requestor: string;
    approvedBy: string | null;
    status: string;
    versions: number;
    responseHours: number | null;
    sendDate: string | null;
    latest: DashboardApprovalRow;
}

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false,
})
export class DashboardComponent extends BaseComponent implements AfterViewChecked, OnDestroy {
    public mainTab: 'overview' | 'analytics' = 'overview';
    public overviewView: SubView = 'requests';
    public analyticsView: SubView = 'requests';

    public requests: DashboardRequestRow[] = [];
    public approvals: DashboardApprovalRow[] = [];

    public canApprove = false;
    public loadingRequests = false;
    public loadingApprovals = false;
    public requestsError = '';
    public approvalsError = '';

    public userName = '';
    public userInitials = '';
    public userPhoto = '';
    public showPhoto = false;

    private expandedKey: string | null = null;
    private expandedReminderKey: string | null = null;

    // ---- Requestor analytics --------------------------------------------
    public analyticsLoading = false;
    public analyticsError = '';
    private recipientsResolved = false;

    public totalCampaigns = 0;
    public approvedCount = 0;
    public rejectedCount = 0;
    public canceledCount = 0;
    public completedCount = 0;
    public abandonedCount = 0;
    public submittedCount = 0;

    public activeCount = 0;
    public scheduledCount = 0;

    public totalRecipients = 0;
    public avgAudienceSize = 0;
    private recipientBars: RecipientBar[] = [];

    // ---- Approver analytics ---------------------------------------------
    public totalAssigned = 0;
    public apprApprovedCount = 0;
    public apprRejectedCount = 0;
    public apprCanceledCount = 0;
    public apprCompletedCount = 0;
    public apprAbandonedCount = 0;
    public apprPendingCount = 0;
    public approvedToDate = 0;
    public avgResponseHours = 0;
    private responseBuckets: number[] = [0, 0, 0, 0, 0];
    private approvedTrend: { date: string; count: number }[] = [];

    // ---- Approver per-campaign drilldown --------------------------------
    public approverCampaigns: ApproverCampaign[] = [];
    public selectedCampaign: ApproverCampaign | null = null;
    public selectedRecipients: number | null = null;
    public selectedRecipientsLoading = false;

    private charts: Chart<any, any, any>[] = [];
    private pendingRender = false;

    @ViewChild('statusCanvas') statusCanvas?: ElementRef<HTMLCanvasElement>;
    @ViewChild('scheduleCanvas') scheduleCanvas?: ElementRef<HTMLCanvasElement>;
    @ViewChild('recipientsCanvas') recipientsCanvas?: ElementRef<HTMLCanvasElement>;
    @ViewChild('outcomeCanvas') outcomeCanvas?: ElementRef<HTMLCanvasElement>;
    @ViewChild('responseCanvas') responseCanvas?: ElementRef<HTMLCanvasElement>;
    @ViewChild('trendCanvas') trendCanvas?: ElementRef<HTMLCanvasElement>;

    constructor(
        private api: ApiService,
        private router: Router,
        private graphService: GraphService,
        private appSettingsService: AppSettingsService,
    ) {
        super();
    }

    //*************************************************************************
    //  Component Life-Cycle Methods
    //*************************************************************************
    public override async ngOnInit() {
        await super.ngOnInit();

        this.title = 'Dashboard';
        this.isLoading = false;

        this.loadUserInfo();
        this.loadRequests();
        this.loadApprovals();
    }

    //*************************************************************************
    //  Data Loading
    //*************************************************************************
    private async loadUserInfo(): Promise<void> {
        const settings = await this.appSettingsService.getSettings();
        const user = settings.user;
        if (!user) {
            return;
        }

        this.userName = user.name || '';
        this.userInitials = user.initials || '';

        if (user.photo) {
            this.userPhoto = user.photo;
            this.showPhoto = true;
            return;
        }

        this.graphService.getUserPhoto().then((photo: any) => {
            if (photo && photo !== 'x') {
                this.userPhoto = photo;
                this.showPhoto = true;
                user.photo = photo;
                this.appSettingsService.saveSettings(settings);
            }
        });
    }

    public loadRequests(): void {
        this.loadingRequests = true;
        this.requestsError = '';
        this.api.getDashboardRequests().subscribe({
            next: (rows) => {
                this.requests = rows || [];
                this.loadingRequests = false;
                this.refreshAnalyticsWhenReady();
            },
            error: (err) => {
                this.requestsError = this.describeError(err);
                this.loadingRequests = false;
                this.refreshAnalyticsWhenReady();
            },
        });
    }

    public loadApprovals(): void {
        this.loadingApprovals = true;
        this.approvalsError = '';
        this.api.getDashboardApprovals().subscribe({
            next: (rows) => {
                this.approvals = rows || [];
                this.canApprove = true;
                this.loadingApprovals = false;
                this.refreshAnalyticsWhenReady();
            },
            error: (err) => {
                // A 403 means the user is not a valid approver; hide the tab.
                if (err?.status === 403) {
                    this.canApprove = false;
                } else {
                    this.approvalsError = this.describeError(err);
                }
                this.loadingApprovals = false;
                this.refreshAnalyticsWhenReady();
            },
        });
    }

    //*************************************************************************
    //  UI Helpers
    //*************************************************************************
    public setMainTab(tab: 'overview' | 'analytics'): void {
        this.mainTab = tab;
        if (tab === 'analytics') {
            this.ensureAnalytics();
        }
    }

    public setOverviewView(view: string): void {
        this.overviewView = view === 'approvals' && this.canApprove ? 'approvals' : 'requests';
    }

    public setAnalyticsView(view: string): void {
        this.analyticsView = view === 'approvals' && this.canApprove ? 'approvals' : 'requests';
        this.selectedCampaign = null;
        this.pendingRender = true;
    }

    public goHome(): void {
        this.router.navigate(['/']);
    }

    public toggleDetails(row: DashboardApprovalRow | DashboardRequestRow): void {
        const key = this.rowKey(row);
        this.expandedKey = this.expandedKey === key ? null : key;
    }

    public isExpanded(row: DashboardApprovalRow | DashboardRequestRow): boolean {
        return this.expandedKey === this.rowKey(row);
    }

    public toggleReminderDetails(row: DashboardApprovalRow | DashboardRequestRow): void {
        const key = this.rowKey(row);
        this.expandedReminderKey = this.expandedReminderKey === key ? null : key;
    }

    public areRemindersExpanded(row: DashboardApprovalRow | DashboardRequestRow): boolean {
        return this.expandedReminderKey === this.rowKey(row);
    }

    public reminderLabel(reminderNumber: number): string {
        if (reminderNumber === 0) {
            return 'Initial Send out';
        }

        const lastTwoDigits = reminderNumber % 100;
        const suffix = lastTwoDigits >= 11 && lastTwoDigits <= 13
            ? 'th'
            : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[reminderNumber % 10] || 'th';
        return `${reminderNumber}${suffix} Reminder`;
    }

    public statusClass(status: string): string {
        const s = (status || '').toLowerCase();
        switch (s) {
            case 'approved':
                return 'status status-approved';
            case 'rejected':
                return 'status status-rejected';
            case 'canceled':
            case 'cancelled':
                return 'status status-canceled';
            case 'completed':
                return 'status status-completed';
            case 'abandoned':
                return 'status status-abandoned';
            case 'sendfailed':
                return 'status status-rejected';
            case 'submitted':
            default:
                return 'status status-submitted';
        }
    }

    public isApproved(status: string): boolean {
        return (status || '').toLowerCase() === 'approved';
    }

    // Approver-facing label only; requestor view keeps the raw "Submitted" DB value.
    public approvalStatusLabel(status: string): string {
        return (status || '').toLowerCase() === 'submitted' ? 'Awaiting Approval' : status;
    }

    public audienceSummary(audience?: AudienceSelection): string {
        if (!audience) {
            return '—';
        }
        const parts: string[] = [];
        if (audience.teams?.length) {
            parts.push(`Teams: ${audience.teams.join(', ')}`);
        }
        if (audience.locations?.length) {
            parts.push(`Locations: ${audience.locations.join(', ')}`);
        }
        if (audience.inclusions?.length) {
            parts.push(`Included: ${audience.inclusions.join(', ')}`);
        }
        if (audience.exclusions?.length) {
            parts.push(`Excluded: ${audience.exclusions.join(', ')}`);
        }
        return parts.length ? parts.join(' • ') : '—';
    }

    private rowKey(row: DashboardApprovalRow | DashboardRequestRow): string {
        return `${row.campaign_id}:${row.version}`;
    }

    //*************************************************************************
    //  Analytics
    //*************************************************************************
    public ngAfterViewChecked(): void {
        // Charts can only be created once the *ngIf canvases exist in the DOM.
        if (this.mainTab !== 'analytics' || !this.pendingRender) {
            return;
        }
        const ready = this.analyticsView === 'requests' ? !!this.statusCanvas : !!this.outcomeCanvas;
        if (ready) {
            this.pendingRender = false;
            this.renderCharts();
        }
    }

    public ngOnDestroy(): void {
        this.destroyCharts();
    }

    private ensureAnalytics(): void {
        if (this.loadingRequests || this.loadingApprovals) {
            this.analyticsLoading = true;
            return;
        }

        this.analyticsLoading = false;
        this.computeRequestorMetrics();
        this.computeApproverMetrics();
        this.buildApproverCampaigns();
        if (!this.recipientsResolved) {
            this.resolveRequestorRecipients();
        }
        this.pendingRender = true;
    }

    private refreshAnalyticsWhenReady(): void {
        if (
            this.mainTab === 'analytics' &&
            !this.loadingRequests &&
            !this.loadingApprovals
        ) {
            this.ensureAnalytics();
        }
    }

    private campaignSource(): DashboardRequestRow[] {
        // Latest version per campaign, so multi-version campaigns count once.
        const latest = new Map<string, DashboardRequestRow>();
        for (const row of this.requests) {
            const existing = latest.get(row.campaign_id);
            if (!existing || row.version > existing.version) {
                latest.set(row.campaign_id, row);
            }
        }
        return Array.from(latest.values());
    }

    // ---- Requestor metrics (from the user's own requests) ----------------
    private computeRequestorMetrics(): void {
        const rows = this.campaignSource();
        this.totalCampaigns = rows.length;
        this.approvedCount = 0;
        this.rejectedCount = 0;
        this.canceledCount = 0;
        this.completedCount = 0;
        this.abandonedCount = 0;
        this.submittedCount = 0;
        this.activeCount = 0;
        this.scheduledCount = 0;
        const now = Date.now();
        for (const row of rows) {
            const s = (row.status || '').toLowerCase();
            switch (s) {
                case 'rejected':
                    this.rejectedCount++;
                    break;
                case 'canceled':
                case 'cancelled':
                    this.canceledCount++;
                    break;
                case 'completed':
                    this.completedCount++;
                    break;
                case 'abandoned':
                    this.abandonedCount++;
                    break;
                case 'approved':
                    this.approvedCount++;
                    break;
                case 'sendfailed':
                    break;
                default:
                    this.submittedCount++;
                    break;
            }

            // Active vs scheduled only applies to approved campaigns.
            if (s === 'approved') {
                const when = row.details?.send_datetime
                    ? Date.parse(row.details.send_datetime)
                    : NaN;
                if (!isNaN(when) && when > now) {
                    this.scheduledCount++;
                } else {
                    this.activeCount++;
                }
            }
        }
    }

    // ---- Approver metrics (from approvals assigned to the user) -----------
    private computeApproverMetrics(): void {
        this.responseBuckets = [0, 0, 0, 0, 0];
        this.apprApprovedCount = 0;
        this.apprRejectedCount = 0;
        this.apprCanceledCount = 0;
        this.apprCompletedCount = 0;
        this.apprAbandonedCount = 0;
        this.apprPendingCount = 0;
        const hours: number[] = [];
        const approvedEvents: number[] = [];

        for (const row of this.approvals) {
            const s = (row.status || '').toLowerCase();
            const isApproved = s === 'approved';
            const isRejected = s === 'rejected';
            switch (s) {
                case 'rejected':
                    this.apprRejectedCount++;
                    break;
                case 'canceled':
                case 'cancelled':
                    this.apprCanceledCount++;
                    break;
                case 'completed':
                    this.apprCompletedCount++;
                    break;
                case 'abandoned':
                    this.apprAbandonedCount++;
                    break;
                case 'approved':
                    this.apprApprovedCount++;
                    break;
                default:
                    this.apprPendingCount++;
                    break;
            }

            const created = Date.parse(row.created_at);
            const acted = Date.parse(row.action_at);
            const acted_valid = !isNaN(created) && !isNaN(acted) && acted >= created;

            if ((isApproved || isRejected) && acted_valid) {
                const h = (acted - created) / 3_600_000;
                hours.push(h);
                this.responseBuckets[this.responseBucketIndex(h)]++;
            }
            if (isApproved && !isNaN(acted)) {
                approvedEvents.push(acted);
            }
        }


        this.approvedToDate = this.apprApprovedCount;
        this.avgResponseHours = hours.length
            ? hours.reduce((a, b) => a + b, 0) / hours.length
            : 0;

        // Cumulative approvals over time, grouped by day.
        approvedEvents.sort((a, b) => a - b);
        const byDay = new Map<string, number>();
        for (const ts of approvedEvents) {
            const day = new Date(ts).toISOString().slice(0, 10);
            byDay.set(day, (byDay.get(day) || 0) + 1);
        }
        let cumulative = 0;
        this.approvedTrend = Array.from(byDay.entries()).map(([date, n]) => {
            cumulative += n;
            return { date, count: cumulative };
        });
    }

    private buildApproverCampaigns(): void {
        const groups = new Map<string, DashboardApprovalRow[]>();
        for (const row of this.approvals) {
            const arr = groups.get(row.campaign_id) || [];
            arr.push(row);
            groups.set(row.campaign_id, arr);
        }

        const list: ApproverCampaign[] = [];
        for (const [campaignId, rows] of groups) {
            const latest = rows.reduce((a, b) => (b.version > a.version ? b : a));
            const created = Date.parse(latest.created_at);
            const acted = Date.parse(latest.action_at);
            const responseHours =
                !isNaN(created) && !isNaN(acted) && acted >= created
                    ? (acted - created) / 3_600_000
                    : null;
            list.push({
                campaignId,
                name: latest.campaign_name || latest.campaign_alias || 'Untitled campaign',
                requestor: latest.requestor || '—',
                approvedBy: latest.approved_by,
                status: latest.status,
                versions: rows.length,
                responseHours,
                sendDate: latest.details?.send_datetime || null,
                latest,
            });
        }

        list.sort((a, b) => a.name.localeCompare(b.name));
        this.approverCampaigns = list;
        this.totalAssigned = list.length;
    }

    public selectCampaign(campaign: ApproverCampaign): void {
        // Toggle: clicking the open campaign collapses it.
        if (this.selectedCampaign?.campaignId === campaign.campaignId) {
            this.selectedCampaign = null;
            return;
        }
        this.selectedCampaign = campaign;
        this.selectedRecipients = null;
        this.selectedRecipientsLoading = true;
        this.api
            .resolveAudience(campaign.latest.details.audience)
            .pipe(catchError(() => of({ total: 0, people: [] })))
            .subscribe((res) => {
                this.selectedRecipients = res?.total ?? 0;
                this.selectedRecipientsLoading = false;
            });
    }

    public isCampaignOpen(campaign: ApproverCampaign): boolean {
        return this.selectedCampaign?.campaignId === campaign.campaignId;
    }

    private responseBucketIndex(h: number): number {
        if (h < 1) return 0;
        if (h < 4) return 1;
        if (h < 24) return 2;
        if (h < 72) return 3;
        return 4;
    }

    private resolveRequestorRecipients(): void {
        const rows = this.campaignSource();
        if (rows.length === 0) {
            this.recipientsResolved = true;
            this.pendingRender = true;
            return;
        }
        this.analyticsLoading = true;
        this.analyticsError = '';

        const calls = rows.map((row) =>
            this.api.resolveAudience(row.audience).pipe(
                map((res) => ({ name: row.campaign_name || row.campaign_alias || 'Untitled campaign', count: res?.total || 0 })),
                catchError(() => of({ name: row.campaign_name || row.campaign_alias || 'Untitled campaign', count: 0 })),
            ),
        );

        forkJoin(calls).subscribe({
            next: (results) => {
                this.recipientBars = results
                    .filter((r) => r.count > 0)
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 8);
                this.totalRecipients = results.reduce((sum, r) => sum + r.count, 0);
                this.avgAudienceSize = results.length
                    ? Math.round(this.totalRecipients / results.length)
                    : 0;
                this.recipientsResolved = true;
                this.analyticsLoading = false;
                this.pendingRender = true;
            },
            error: (err) => {
                this.analyticsError = this.describeError(err);
                this.analyticsLoading = false;
                this.recipientsResolved = true;
                this.pendingRender = true;
            },
        });
    }

    private destroyCharts(): void {
        for (const chart of this.charts) {
            chart.destroy();
        }
        this.charts = [];
    }

    private renderCharts(): void {
        this.destroyCharts();

        const legendBottom = {
            plugins: {
                legend: { position: 'bottom' as const, labels: { boxWidth: 12, font: { size: 11 } } },
            },
        };

        if (this.analyticsView === 'requests') {
            this.renderRequestorCharts(legendBottom);
        } else {
            this.renderApproverCharts(legendBottom);
        }
    }

    private renderRequestorCharts(legendBottom: any): void {
        // 1) Campaign status breakdown.
        if (this.statusCanvas) {
            const statusConfig: ChartConfiguration<'doughnut', number[], string> = {
                type: 'doughnut',
                data: {
                    labels: ['Approved', 'Rejected', 'Canceled', 'Completed', 'Abandoned', 'Submitted'],
                    datasets: [{
                        data: [
                            this.approvedCount,
                            this.rejectedCount,
                            this.canceledCount,
                            this.completedCount,
                            this.abandonedCount,
                            this.submittedCount,
                        ],
                        backgroundColor: [
                            CHEVRON.mediumGreen,
                            CHEVRON.lightRed,
                            CHEVRON.gray,
                            CHEVRON.mediumBlue,
                            CHEVRON.lightGray,
                            CHEVRON.orange,
                        ],
                        borderWidth: 0,
                    }],
                },
                options: { responsive: true, maintainAspectRatio: false, cutout: '62%', ...legendBottom },
            };
            this.charts.push(new Chart(this.statusCanvas.nativeElement, statusConfig));
        }

        // 2) Active vs scheduled.
        if (this.scheduleCanvas) {
            const scheduleConfig: ChartConfiguration<'doughnut', number[], string> = {
                type: 'doughnut',
                data: {
                    labels: ['Active', 'Scheduled'],
                    datasets: [{
                        data: [this.activeCount, this.scheduledCount],
                        backgroundColor: [CHEVRON.mediumBlue, CHEVRON.lightBlue],
                        borderWidth: 0,
                    }],
                },
                options: { responsive: true, maintainAspectRatio: false, cutout: '62%', ...legendBottom },
            };
            this.charts.push(new Chart(this.scheduleCanvas.nativeElement, scheduleConfig));
        }

        // 3) Recipients per campaign (top campaigns by audience size).
        if (this.recipientsCanvas) {
            const fullNames = this.recipientBars.map((r) => r.name);
            const truncate = (name: string, max = 22): string =>
                name.length > max ? `${name.slice(0, max - 1)}…` : name;

            const config: ChartConfiguration = {
                type: 'bar',
                data: {
                    labels: fullNames,
                    datasets: [{
                        label: 'Recipients',
                        data: this.recipientBars.map((r) => r.count),
                        backgroundColor: CHEVRON.lightBlue,
                        borderRadius: 4,
                    }],
                },
                options: {
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            // Always show the full campaign name on hover, even when truncated on the axis.
                            callbacks: { title: (items) => fullNames[items[0]?.dataIndex ?? 0] || '' },
                        },
                    },
                    scales: {
                        x: { beginAtZero: true, ticks: { precision: 0 } },
                        y: {
                            ticks: {
                                autoSkip: false,
                                callback: (_value, index) => truncate(fullNames[index] ?? ''),
                            },
                        },
                    },
                },
            };
            this.charts.push(new Chart(this.recipientsCanvas.nativeElement, config));
        }
    }

    private renderApproverCharts(legendBottom: any): void {
        // 1) Approval outcome breakdown.
        if (this.outcomeCanvas) {
            const outcomeConfig: ChartConfiguration<'doughnut', number[], string> = {
                type: 'doughnut',
                data: {
                    labels: ['Approved', 'Rejected', 'Canceled', 'Completed', 'Abandoned', 'Awaiting Approval'],
                    datasets: [{
                        data: [
                            this.apprApprovedCount,
                            this.apprRejectedCount,
                            this.apprCanceledCount,
                            this.apprCompletedCount,
                            this.apprAbandonedCount,
                            this.apprPendingCount,
                        ],
                        backgroundColor: [
                            CHEVRON.mediumGreen,
                            CHEVRON.lightRed,
                            CHEVRON.gray,
                            CHEVRON.mediumBlue,
                            CHEVRON.lightGray,
                            CHEVRON.orange,
                        ],
                        borderWidth: 0,
                    }],
                },
                options: { responsive: true, maintainAspectRatio: false, cutout: '62%', ...legendBottom },
            };
            this.charts.push(new Chart(this.outcomeCanvas.nativeElement, outcomeConfig));
        }

        // 2) Approval response-time distribution.
        if (this.responseCanvas) {
            this.charts.push(new Chart(this.responseCanvas.nativeElement, {
                type: 'bar',
                data: {
                    labels: ['< 1h', '1–4h', '4–24h', '1–3d', '> 3d'],
                    datasets: [{
                        label: 'Approvals',
                        data: this.responseBuckets,
                        backgroundColor: CHEVRON.mediumBlue,
                        borderRadius: 4,
                    }],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                },
            }));
        }

        // 3) Cumulative approvals to date.
        if (this.trendCanvas) {
            const trendConfig: ChartConfiguration<'line', number[], string> = {
                type: 'line',
                data: {
                    labels: this.approvedTrend.map((p) => p.date),
                    datasets: [{
                        label: 'Approved (cumulative)',
                        data: this.approvedTrend.map((p) => p.count),
                        borderColor: CHEVRON.mediumBlue,
                        backgroundColor: 'rgba(0, 102, 178, 0.12)',
                        fill: true,
                        tension: 0.3,
                        pointRadius: 2,
                    }],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                },
            };
            this.charts.push(new Chart(this.trendCanvas.nativeElement, trendConfig));
        }
    }

    public formatHours(h: number | null): string {
        if (h === null || isNaN(h)) {
            return '—';
        }
        if (h < 1) {
            return `${Math.round(h * 60)} min`;
        }
        if (h < 48) {
            return `${h.toFixed(1)} h`;
        }
        return `${(h / 24).toFixed(1)} d`;
    }

    private describeError(err: any): string {
        if (err?.error?.detail) {
            return err.error.detail;
        }
        if (err?.message) {
            return err.message;
        }
        return 'Failed to load data.';
    }
}
